import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { ui, serviceCategories, contact } from '../data/content';

const SRTECH_LOGO = 'https://customer-assets-39nsmqrw.emergentagent.net/job_ranjeet-services-1/artifacts/09f8rimg_file_0000000048fc71fa8691cce128530a1a.png';

const DivineLogo = () => (
  <div style={{
    width: '46px', height: '46px', borderRadius: '50%', overflow: 'hidden',
    border: '2px solid rgba(255,215,0,0.6)',
    boxShadow: '0 0 12px rgba(255,215,0,0.5)',
    flexShrink: 0,
  }}>
    <img
      src={SRTECH_LOGO}
      alt="SR Tech Logo"
      style={{ width: '100%', height: '160%', objectFit: 'cover', objectPosition: '50% 5%', display: 'block', marginTop: '-4px' }}
    />
  </div>
);

export default function Header() {
  const { lang, toggle, isHindi } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { key: 'home', id: 'home' },
    { key: 'services', id: 'services' },
    { key: 'about', id: 'about' },
    { key: 'contact', id: 'contact' },
  ];

  return (
    <header
      data-testid="main-header"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'rgba(10,17,40,0.97)' : 'rgba(10,17,40,0.88)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,215,0,0.2)',
        boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.5)' : 'none',
        transition: 'background 0.3s, box-shadow 0.3s',
      }}
    >
      {/* Top ticker */}
      <div style={{ background: 'rgba(255,153,0,0.12)', borderBottom: '1px solid rgba(255,153,0,0.15)', padding: '5px 0', overflow: 'hidden' }}>
        <div className="ticker-content" style={{ fontSize: '11px', color: '#FF9933', fontFamily: 'Khand, sans-serif', letterSpacing: '1px' }}>
          &nbsp;&nbsp;🚩 PAN Card &nbsp;✦&nbsp; आधार Card &nbsp;✦&nbsp; पासपोर्ट &nbsp;✦&nbsp; Voter ID &nbsp;✦&nbsp; GST Registration &nbsp;✦&nbsp; Driving Licence &nbsp;✦&nbsp; Vehicle Insurance &nbsp;✦&nbsp; Wedding Video &nbsp;✦&nbsp; Business Website &nbsp;✦&nbsp; Logo Design &nbsp;✦&nbsp; Religious Tours &nbsp;✦&nbsp; Flight Booking &nbsp;✦&nbsp; Android App &nbsp;✦&nbsp; Trademark &nbsp;✦&nbsp; E-Shram Card &nbsp;✦&nbsp; ITR Filing &nbsp;✦&nbsp; Certificate Design &nbsp;✦&nbsp;
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Left: Logo + Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => scrollTo('home')}>
          <div className="logo-divine"><DivineLogo /></div>
          <div>
            <div className="hindi divine-glow-text" style={{ fontSize: 'clamp(13px,2.5vw,19px)', fontWeight: 700, color: '#FFD700', lineHeight: 1.1 }}>
              श्री रणजीत सर्विसेस
            </div>
            <div style={{ fontFamily: '"Cinzel Decorative", serif', fontSize: 'clamp(7px,1.2vw,9px)', color: '#FF9933', letterSpacing: '1.5px', lineHeight: 1 }}>
              SHRI RANJEET SERVICES
            </div>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: '6px' }} className="hidden-mobile">
          {navLinks.map(link => (
            <button key={link.key} onClick={() => scrollTo(link.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#FFF3B0', fontFamily: 'Rajdhani, sans-serif', fontSize: '15px',
                fontWeight: 600, padding: '6px 14px', borderRadius: '6px',
                letterSpacing: '0.5px',
                transition: 'color 0.2s, background 0.2s',
              }}
              onMouseEnter={e => { e.target.style.color = '#FFD700'; e.target.style.background = 'rgba(255,215,0,0.08)'; }}
              onMouseLeave={e => { e.target.style.color = '#FFF3B0'; e.target.style.background = 'none'; }}>
              {ui.nav[link.key][lang]}
            </button>
          ))}
        </nav>

        {/* Right: Language + Phone + Hands */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Language Toggle */}
          <button data-testid="lang-toggle" onClick={toggle}
            style={{
              background: 'rgba(255,215,0,0.12)', border: '1px solid rgba(255,215,0,0.35)',
              color: '#FFD700', padding: '5px 12px', borderRadius: '20px',
              cursor: 'pointer', fontFamily: 'Khand, sans-serif', fontWeight: 700, fontSize: '13px',
              letterSpacing: '1px',
            }}>
            {isHindi ? 'EN' : 'हि'}
          </button>

          {/* Phone */}
          <a href={`tel:${contact.phone}`} data-testid="header-phone"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#FF9933', textDecoration: 'none', fontSize: '14px', fontFamily: 'Rajdhani, sans-serif', fontWeight: 600 }}
            className="hidden-mobile">
            <i className="fa-solid fa-phone" style={{ fontSize: '12px' }} />
            {contact.phone}
          </a>

          {/* Animated hands */}
          <div className="animate-float hidden-mobile" style={{ fontSize: '22px', lineHeight: 1 }}>🙏</div>

          {/* Mobile menu button */}
          <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)',
              color: '#FFD700', padding: '7px 10px', borderRadius: '8px', cursor: 'pointer',
              display: 'none',
            }}
            className="show-mobile">
            <i className={`fa-solid fa-${menuOpen ? 'xmark' : 'bars'}`} style={{ fontSize: '16px' }} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div ref={menuRef}
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}
            style={{ background: 'rgba(10,17,40,0.98)', borderTop: '1px solid rgba(255,215,0,0.15)', overflow: 'hidden' }}>
            {navLinks.map(link => (
              <button key={link.key} onClick={() => scrollTo(link.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '14px 24px', background: 'none', border: 'none',
                  color: '#FFF3B0', fontFamily: 'Khand, sans-serif', fontSize: '16px',
                  cursor: 'pointer', borderBottom: '1px solid rgba(255,215,0,0.08)',
                  letterSpacing: '0.5px',
                }}>
                {ui.nav[link.key][lang]}
              </button>
            ))}
            <div style={{ padding: '12px 24px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <a href={`tel:${contact.phone}`} style={{ color: '#FF9933', fontSize: '14px', fontFamily: 'Khand, sans-serif' }}>
                <i className="fa-solid fa-phone" style={{ marginRight: '6px' }} />{contact.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .hidden-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
        }
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
          .hidden-mobile { display: flex !important; }
        }
      `}</style>
    </header>
  );
}
